import React, { useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { 
  Home, 
  Search, 
  Bookmark, 
  FileText, 
  File, 
  Bell, 
  Settings, 
  Building2,
  Menu,
  X,
  Globe,
  LogOut
} from 'lucide-react';
import { Button } from '../ui/button';
import { useApp } from '@/pages/providers/AppProvider';
import { useAuth } from '@/components/auth/AuthContext';

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  badge?: number;
}

export function JobSeekerDashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { applications, savedJobs } = useApp();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const isCollapsed = !sidebarOpen;

  const menuItems: MenuItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <Home className="h-5 w-5" />,
      path: '/jobseeker-dashboard'
    },
    {
      id: 'jobs',
      label: 'Jobs',
      icon: <Search className="h-5 w-5" />,
      path: '/jobseeker-dashboard/jobs'
    },
    {
      id: 'saved',
      label: 'Saved Jobs',
      icon: <Bookmark className="h-5 w-5" />,
      path: '/jobseeker-dashboard/saved',
      badge: savedJobs?.length || 0
    },
    {
      id: 'applications',
      label: 'Applications',
      icon: <FileText className="h-5 w-5" />,
      path: '/jobseeker-dashboard/applications',
      badge: applications?.length || 0
    },
    {
      id: 'resume',
      label: 'Resume',
      icon: <File className="h-5 w-5" />,
      path: '/jobseeker-dashboard/resume'
    },
    {
      id: 'alerts',
      label: 'Alerts',
      icon: <Bell className="h-5 w-5" />,
      path: '/jobseeker-dashboard/alerts'
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: <Settings className="h-5 w-5" />,
      path: '/jobseeker-dashboard/settings'
    },
    {
      id: 'following',
      label: 'Following',
      icon: <Building2 className="h-5 w-5" />,
      path: '/jobseeker-dashboard/following'
    }
  ];

  const isActive = (path: string) => {
    if (path === '/jobseeker-dashboard') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleBackToWebsite = () => {
    navigate('/');
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'w-64' : 'w-16'
        } bg-white border-r border-gray-200 transition-all duration-300 flex flex-col overflow-hidden`}
      >
        {/* Logo/Brand */}
        <div className={`border-b border-gray-200 ${isCollapsed ? 'p-2' : 'p-6'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {!isCollapsed && (
                <img
                  src="/CompanyLogo.png"
                  alt="Company Logo"
                  className="h-8 w-auto"
                />
              )}
            </div>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-1 hover:bg-gray-100 rounded"
              aria-label="Toggle sidebar"
              title={sidebarOpen ? 'Collapse' : 'Expand'}
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
          {!isCollapsed && (
            <p className="text-xs text-gray-500 mt-1">JOB CLOSE TO YOUR CAREER</p>
          )}
        </div>

        {/* Menu Items */}
        <nav className="flex-1 overflow-y-auto py-4">
          <ul className={`space-y-1 ${isCollapsed ? 'px-1' : 'px-3'}`}>
            {menuItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => handleNavigate(item.path)}
                  className={`w-full flex items-center ${isCollapsed ? 'justify-center px-2 py-3' : 'gap-3 px-4 py-3'} rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className={`${isActive(item.path) ? 'text-blue-600' : 'text-gray-500'} ${isCollapsed ? '' : ''}`}>
                    {item.icon}
                  </span>
                  {!isCollapsed && <span className="font-medium">{item.label}</span>}
                  {!isCollapsed && item.badge !== undefined && item.badge > 0 && (
                    <span className="ml-auto bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Bottom Actions */}
        <div className={`border-t border-gray-200 ${isCollapsed ? 'p-2' : 'p-4'} space-y-2`}>
          <Button
            variant="outline"
            size="sm"
            className={`w-full ${isCollapsed ? 'justify-center text-red-600 hover:text-red-700' : 'justify-start text-red-600 hover:text-red-700'}`}
            onClick={handleLogout}
            title={isCollapsed ? 'Logout' : undefined}
          >
            <LogOut className={`h-4 w-4 ${isCollapsed ? '' : 'mr-2'}`} />
            {!isCollapsed && 'Logout'}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-semibold text-gray-900">
                {menuItems.find((item) => isActive(item.path))?.label || 'Dashboard'}
              </h1>
            </div>
            
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={handleBackToWebsite}>
                <Globe className="h-4 w-4 mr-2" />
                Back to Website
              </Button>
              <Button variant="ghost" size="sm">
                <Bell className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <div className="max-w-7xl mx-auto p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
